/**
 * @author Maxime Mustarda <maxime@inarix.com>
 * @file LicensePacksModal.tsx
 * @desc Created on Sat May 17 2025 02:00:21
 * @copyright All rights reserved @ Inarix
 */

import { FC, useState } from "react";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  Snackbar,
} from "@mui/material";
import {
  focusedLicense,
  selectAvailPacks,
  selectModalLicense,
} from "../../redux/slices/licenses/licenseList";
import { useTranslation } from "react-i18next";
import { renderRemainingCredits } from "./LicenceCredits";
import { bindPacks } from "../../redux/actions/licenses/licenseListAsyncActions";
import { licensePacksModalStyles } from "./licenseStyles/LicensePacksModal";

export const LicensePacksModal: FC = () => {
  const packs = useSelector(selectAvailPacks);
  const license = useSelector(selectModalLicense);
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const [t, i18n] = useTranslation("global");
  const [checked, setChecked] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [errorOpen, setErrorOpen] = useState(false);
  const checkHandler = (ev: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked: isChecked } = ev.target;
    setChecked((prev) =>
      isChecked ? [...prev, value] : prev.filter((id) => id !== value),
    );
  };

  // type-safe if, won't ever happen
  if (!license) {
    return <></>;
  }

  const sortedPacks = [...packs]
    .filter((p) => new Date(p.expiryDate).getTime() >= Date.now())
    .sort((a, b) => {
      if (b.initialCredits !== a.initialCredits) {
        return b.initialCredits - a.initialCredits;
      }
      return (
        new Date(a.expiryDate).getTime() - new Date(b.expiryDate).getTime()
      );
    });

  return (
    <Box sx={licensePacksModalStyles.modalBox}>
      <div className="modal-detail-user-container">
        <div>
          <div className="username-title-modal">{t("licenses.addCreds")}</div>
        </div>

        <div
          className="access-and-permissions"
          style={licensePacksModalStyles.accessContainer}
        >
          <div className="access-title">{t("licenses.License")}</div>
          <div className="access-perm-info">
            <div className="access-values-users">
              <div className="option-left mt-2">
                <span className="title-view-span">
                  {t("licenses.remaining")}
                </span>
              </div>
              <div className="option-right">
                {renderRemainingCredits(license, t, i18n.language)}
              </div>
            </div>
            <div className="access-values-users">
              <div className="option-left">
                <span className="title-view-span">
                  {t("licenses.expiration")}
                </span>
              </div>
              <div className="option-right">
                {new Date(license.expiryDate).toLocaleDateString(i18n.language)}
              </div>
            </div>
            <div className="access-value-users">&nbsp;</div>
          </div>
          <div className="divider-user-details" />

          <FormControl sx={licensePacksModalStyles.formControl}>
            <FormGroup onChange={checkHandler}>
              {sortedPacks?.map((p) => (
                <FormControlLabel
                  label={`+ ${p.initialCredits} ${t("licenses.credits")} - ${t("licenses.pack_expiration")} : ${new Date(p.expiryDate).toLocaleDateString(i18n.language)}`}
                  control={
                    <Checkbox
                      value={p.id}
                      checked={checked.includes(p.id)}
                      disabled={loading}
                    />
                  }
                  key={p.id}
                  slotProps={{
                    typography: { fontSize: "12px" },
                  }}
                />
              ))}
            </FormGroup>
          </FormControl>

          {errorOpen && (
            <Alert
              severity="error"
              onClose={() => setErrorOpen(false)}
              sx={{ mt: 1, mx: 2 }}
            >
              {t("Common_options.error")}
            </Alert>
          )}
        </div>

        <div className="buttons-user-view">
          <div className="container-buttons-users-actions">
            <Button
              disabled={loading || successOpen}
              variant="outlined"
              onClick={() => dispatch(focusedLicense(""))}
              sx={licensePacksModalStyles.buttonTextTransform}
            >
              {t("Common_options.Cancel")}
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                setLoading(true);
                setErrorOpen(false);
                dispatch(bindPacks({ licenseId: license.id, packIds: checked }))
                  .unwrap()
                  .then(() => {
                    setSuccessOpen(true);
                    setTimeout(() => {
                      dispatch(focusedLicense(""));
                    }, 1500);
                  })
                  .catch(() => {
                    setErrorOpen(true);
                  })
                  .finally(() => {
                    setLoading(false);
                  });
              }}
              sx={licensePacksModalStyles.buttonTextTransform}
              disabled={checked.length === 0 || loading || successOpen}
              startIcon={
                loading ? <CircularProgress size={16} color="inherit" /> : null
              }
            >
              {t("Common_options.Add")}
            </Button>
          </div>
        </div>
      </div>
      <Snackbar
        open={successOpen}
        autoHideDuration={4000}
        onClose={() => setSuccessOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setSuccessOpen(false)} severity="success">
          {t("licenses.pack_added_success")}
        </Alert>
      </Snackbar>
    </Box>
  );
};
